// Alert Service for fetching and managing asset alerts

import type { AssetAlert, AlertSummary, AlertFilters } from '@/types/alerts';
import type { Asset } from '@/types/asset';
import { differenceInDays, parseISO } from 'date-fns';
import { authFetch } from '@/lib/auth-fetch';
import { getBackendUrl } from '@/hooks/use-get-backend-url';

/**
 * Generate alerts for an asset based on compliance, check-in/check-out, and other data
 */
export function generateAlertsForAsset(asset: Asset): AssetAlert[] {
  const alerts: AssetAlert[] = [];
  const now = new Date();

  // Check for insurance expiry alerts
  if (asset.insuranceExpiryDate) {
    const expiryDate = parseISO(asset.insuranceExpiryDate);
    const daysUntilExpiry = differenceInDays(expiryDate, now);
    
    if (daysUntilExpiry < 0) {
      alerts.push({
        id: `${asset.id}-insurance-expired`,
        assetId: asset.id,
        type: 'insurance',
        severity: 'critical',
        status: 'active',
        title: 'Insurance Expired',
        description: `Insurance policy has expired ${Math.abs(daysUntilExpiry)} days ago`,
        createdDate: new Date().toISOString(),
        dueDate: asset.insuranceExpiryDate,
        daysOverdue: Math.abs(daysUntilExpiry),
        insuranceData: {
          policyNumber: asset.insurancePolicyNumber,
          provider: asset.insuranceProvider,
          expiryDate: asset.insuranceExpiryDate,
          daysUntilExpiry,
        },
        actionLabel: 'Renew Insurance',
        canNavigate: true,
      });
    } else if (daysUntilExpiry <= 30) {
      alerts.push({
        id: `${asset.id}-insurance-expiring`,
        assetId: asset.id,
        type: 'insurance',
        severity: daysUntilExpiry <= 7 ? 'critical' : 'warning',
        status: 'active',
        title: 'Insurance Expiring Soon',
        description: `Insurance policy expires in ${daysUntilExpiry} days`,
        createdDate: new Date().toISOString(),
        dueDate: asset.insuranceExpiryDate,
        insuranceData: {
          policyNumber: asset.insurancePolicyNumber,
          provider: asset.insuranceProvider,
          expiryDate: asset.insuranceExpiryDate,
          daysUntilExpiry,
        },
        actionLabel: 'Renew Insurance',
        canNavigate: true,
      });
    }
  }

  // Check for warranty expiry alerts
  if (asset.warrantyExpiryDate) {
    const expiryDate = parseISO(asset.warrantyExpiryDate);
    const daysUntilExpiry = differenceInDays(expiryDate, now);
    
    if (daysUntilExpiry < 0) {
      alerts.push({
        id: `${asset.id}-warranty-expired`,
        assetId: asset.id,
        type: 'warranty',
        severity: 'warning',
        status: 'active',
        title: 'Warranty Expired',
        description: `Warranty has expired ${Math.abs(daysUntilExpiry)} days ago`,
        createdDate: new Date().toISOString(),
        dueDate: asset.warrantyExpiryDate,
        daysOverdue: Math.abs(daysUntilExpiry),
        warrantyData: {
          provider: asset.warrantyProvider,
          expiryDate: asset.warrantyExpiryDate,
          daysUntilExpiry,
        },
        actionLabel: 'Extend Warranty',
        canNavigate: true,
      });
    } else if (daysUntilExpiry <= 30) {
      alerts.push({
        id: `${asset.id}-warranty-expiring`,
        assetId: asset.id,
        type: 'warranty',
        severity: 'info',
        status: 'active',
        title: 'Warranty Expiring Soon',
        description: `Warranty expires in ${daysUntilExpiry} days`,
        createdDate: new Date().toISOString(),
        dueDate: asset.warrantyExpiryDate,
        warrantyData: {
          provider: asset.warrantyProvider,
          expiryDate: asset.warrantyExpiryDate,
          daysUntilExpiry,
        },
        actionLabel: 'Extend Warranty',
        canNavigate: true,
      });
    }
  }

  return alerts;
}

/**
 * Fetch compliance alerts for an asset from the backend API
 */
export async function getComplianceAlertsForAsset(assetId: string): Promise<AssetAlert[]> {
  try {
    const backendUrl = getBackendUrl();
    const response = await authFetch(`${backendUrl}/api/compliance/alerts/asset/${assetId}`);
    if (response.ok) {
      const complianceAlerts = await response.json();
      return complianceAlerts.map((ca: Record<string, unknown>) => ({
        id: ca.id as string,
        assetId: ca.assetId as string,
        type: 'compliance' as const,
        severity: ca.severity as 'critical' | 'warning' | 'info',
        status: ca.status as 'active' | 'acknowledged' | 'resolved',
        title: ca.title as string,
        description: ca.description as string,
        createdDate: ca.createdDate as string,
        dueDate: ca.dueDate as string,
        daysOverdue: ca.daysOverdue as number,
        complianceData: ca.complianceData as AssetAlert['complianceData'],
        actionLabel: ca.actionLabel as string,
        canNavigate: ca.canNavigate as boolean,
      }));
    }
  } catch (error) {
    console.warn('Failed to fetch compliance alerts:', error);
  }
  return [];
}

/**
 * Fetch alerts for a specific asset
 */
export async function getAlertsForAsset(assetId: string): Promise<AssetAlert[]> {
  try {
    // Try to fetch from API
    const response = await fetch(`/api/Alerts/asset/${assetId}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn('Failed to fetch alerts from API, using mock data:', error);
  }

  // Return empty array as fallback
  return [];
}

/**
 * Get alert summary for an asset by ID
 */
export async function getAlertSummary(assetId: string): Promise<AlertSummary> {
  try {
    // Try to fetch from API
    const response = await fetch(`/api/Alerts/asset/${assetId}/summary`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn('Failed to fetch alert summary from API:', error);
  }

  // Return empty summary as fallback
  return {
    totalCount: 0,
    criticalCount: 0,
    warningCount: 0,
    infoCount: 0,
    byType: {
      audit: 0,
      checkinout: 0,
      maintenance: 0,
      insurance: 0,
      warranty: 0,
      compliance: 0,
    },
  };
}

/**
 * Calculate alert summary from alerts array
 */
export function calculateAlertSummary(alerts: AssetAlert[]): AlertSummary {
  const activeAlerts = alerts.filter(a => a.status === 'active');
  
  return {
    totalCount: activeAlerts.length,
    criticalCount: activeAlerts.filter(a => a.severity === 'critical').length,
    warningCount: activeAlerts.filter(a => a.severity === 'warning').length,
    infoCount: activeAlerts.filter(a => a.severity === 'info').length,
    byType: {
      audit: activeAlerts.filter(a => a.type === 'audit').length,
      checkinout: activeAlerts.filter(a => a.type === 'checkinout').length,
      maintenance: activeAlerts.filter(a => a.type === 'maintenance').length,
      insurance: activeAlerts.filter(a => a.type === 'insurance').length,
      warranty: activeAlerts.filter(a => a.type === 'warranty').length,
      compliance: activeAlerts.filter(a => a.type === 'compliance').length,
    },
  };
}

/**
 * Filter alerts based on criteria
 */
export function filterAlerts(alerts: AssetAlert[], filters: AlertFilters): AssetAlert[] {
  let filtered = [...alerts];

  if (filters.type && filters.type.length > 0) {
    filtered = filtered.filter(a => filters.type!.includes(a.type));
  }

  if (filters.severity && filters.severity.length > 0) {
    filtered = filtered.filter(a => filters.severity!.includes(a.severity));
  }

  if (filters.status && filters.status.length > 0) {
    filtered = filtered.filter(a => filters.status!.includes(a.status));
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(a => 
      a.title.toLowerCase().includes(searchLower) ||
      a.description.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
}

/**
 * Acknowledge an alert
 */
export async function acknowledgeAlert(alertId: string, notes?: string): Promise<void> {
  try {
    await fetch(`/api/Alerts/${alertId}/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    });
  } catch (error) {
    console.error('Failed to acknowledge alert:', error);
    throw error;
  }
}

/**
 * Resolve an alert
 */
export async function resolveAlert(alertId: string, notes?: string): Promise<void> {
  try {
    await fetch(`/api/Alerts/${alertId}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    });
  } catch (error) {
    console.error('Failed to resolve alert:', error);
    throw error;
  }
}
